import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Database, Cloud, Shield, CheckCircle2, TrendingUp, Clock, Heart } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Azure Machine Learning', icon: null, image: '/Azure_machine_learning.jpg' },
  { name: 'Python/Pandas', icon: null, image: '/Python.jpg' },
  { name: 'TensorFlow', icon: null, image: '/Tensorflow.png' },
  { name: 'XGBoost', icon: null, image: '/XGboost.png' },
  { name: 'Azure Functions', icon: null, image: '/Azure_Functions.png' }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '85%',
    label: 'Prediction Accuracy',
    description: 'Heart disease risk prediction up to 30 days in advance'
  },
  {
    icon: Heart,
    value: '20%',
    label: 'Reduction in Readmissions',
    description: 'Through proactive patient identification'
  },
  {
    icon: Clock,
    value: '2 min',
    label: 'Risk Assessment Time',
    description: 'Compared to hours of manual assessment'
  },
  {
    icon: CheckCircle2,
    value: '100+',
    label: 'Clinician Hours Freed',
    description: 'Monthly time saved for direct patient care'
  }
];

const highlights = [
  {
    category: 'Clinical Outcomes',
    items: [
      'Achieved 85% accuracy in predicting heart disease risk up to 30 days in advance through advanced ML algorithms',
      'Reduced cardiac readmissions by 20% through proactive patient identification and early intervention protocols',
      'Delivered risk scores in under 2 minutes compared to hours of manual assessment and calculation'
    ]
  },
  {
    category: 'Operational Efficiency',
    items: [
      'Cut manual chart reviews by 70% by automating risk assessment and patient prioritization workflows',
      'Freed 100+ clinician hours monthly for direct patient care instead of administrative data analysis',
      'Maintained 99.5% system uptime with full HIPAA compliance and enterprise-grade security protocols'
    ]
  }
];

const challenges = [
  {
    title: 'Manual Risk Assessment Inefficiency',
    icon: Brain,
    problem: 'Clinicians were manually analyzing multiple data points blood pressure trends, cholesterol levels, ECG readings, family history, and lifestyle factors using Excel spreadsheets, making comprehensive risk assessment time-consuming and prone to human oversight.',
    solution: 'Our AI specialists developed an automated machine learning pipeline using Azure ML workspace that ingests patient data from multiple sources, performs feature engineering on medical indicators, and generates comprehensive risk scores using ensemble algorithms including XGBoost and Random Forest models.',
    impact: '70% reduction in manual chart reviews'
  },
  {
    title: 'Delayed Risk Identification',
    icon: Clock,
    problem: 'High-risk cardiac patients were often identified only after symptoms became severe, leading to emergency interventions, higher costs, and increased readmission rates.',
    solution: 'We implemented predictive models using TensorFlow and advanced ensemble methods that analyze historical patient data patterns to forecast heart disease risk 30 days in advance. The system automatically flags high-risk patients for proactive clinical review and intervention planning.',
    impact: '30-day advance risk prediction capability'
  },
  {
    title: 'Data Integration and HIPAA Compliance',
    icon: Database,
    problem: 'Patient data was scattered across multiple systems including Electronic Health Records, lab systems, and monitoring devices, while maintaining strict HIPAA compliance requirements for data security and patient privacy.',
    solution: 'Using Azure\'s HIPAA-compliant infrastructure, we created secure data ingestion pipelines that automatically collect, normalize, and encrypt patient data from multiple sources. All data processing occurs within encrypted Azure environments with comprehensive audit trails and access controls.',
    impact: 'Full HIPAA compliance with automated audit trails'
  },
  {
    title: 'Clinical Workflow Integration',
    icon: Shield,
    problem: 'Any predictive system needed to integrate seamlessly with existing clinical workflows without disrupting patient care or creating additional administrative burden for healthcare providers.',
    solution: 'We developed Azure Functions that automatically generate risk assessments and integrate directly with the hospital\'s Electronic Health Record system. Risk scores appear automatically in patient charts with clear, actionable recommendations for clinical teams.',
    impact: 'Seamless EHR integration with zero workflow disruption'
  }
];

const HealthcareAI = () => {
  return (
    <>
      <SEOHead
        title="Healthcare AI Case Study | Agility"
        description="How we implemented AI diagnostics for a healthcare network with 95% accuracy."
        url="https://www.agilitytech.ai/case-studies/healthcare-ai"
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
            alt="Healthcare AI Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Healthcare AI/ML</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Heart Disease Risk Prediction Platform
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Transforming cardiac care through AI-driven predictive analytics, enabling early 
              intervention and improved patient outcomes across a major regional health network.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Healthcare/Cardiology</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">United States</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Enterprise Health Network</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q3 2024 - Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                {tech.image ? (
                  <img 
                    src={tech.image} 
                    alt={tech.name}
                    className="h-6 w-6 object-contain"
                  />
                ) : (
                  <tech.icon className="h-6 w-6 text-teal-400" />
                )}
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
            <div className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
              <img 
                src="/HIPAA_Complaint.jpg" 
                alt="HIPAA Compliant"
                className="h-6 w-6 object-contain"
              />
              <span className="text-gray-300 font-medium">HIPAA Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <Heart className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Manual Risk Assessment Crisis</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A regional health network serving 200,000+ patients across multiple facilities struggled 
                    to proactively identify patients at high risk of heart disease complications. Clinical 
                    teams were manually tracking diverse medical indicators through Excel spreadsheets and 
                    paper records, making it nearly impossible to forecast which patients would require 
                    urgent intervention before symptoms became critical.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">AI-Powered Cardiac Care Revolution</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced manual, time-intensive risk assessment with an intelligent, automated system 
                    that transformed cardiac care delivery across the entire health network. By implementing 
                    cutting-edge machine learning algorithms with enterprise-grade security and HIPAA 
                    compliance, we delivered breakthrough clinical outcomes that exceeded all stakeholder 
                    expectations.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Revolutionize Patient Care?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our healthcare AI specialists to discover how predictive analytics can transform 
              your clinical operations and improve patient outcomes.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HealthcareAI;